import React, { useState, useEffect } from 'react';
import { X, CreditCard, Shield, AlertCircle, Info, Lock, CheckCircle, ArrowRight, UserPlus, Sparkles } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { userHasVerifiedPaymentMethod } from '@/utils/paymentUtils';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';

interface PaymentInfoBannerProps {
  onOpenDashboard?: (tab: string) => void;
}

const PaymentInfoBanner: React.FC<PaymentInfoBannerProps> = ({ onOpenDashboard }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(true);

  useEffect(() => {
    sessionStorage.removeItem('payment_banner_dismissed');
  }, []);

  useEffect(() => {
    const checkPaymentMethod = async () => {
      if (!user) {
        setIsCheckingPayment(false);
        return;
      }

      try {
        const hasMethod = await userHasVerifiedPaymentMethod(user.id);
        setHasPaymentMethod(hasMethod);
      } catch (error) {
        console.error('Error checking payment method:', error);
        setHasPaymentMethod(false);
      } finally {
        setIsCheckingPayment(false);
      }
    };

    checkPaymentMethod();
    
    // REAL-TIME: Set up polling and subscription for instant updates
    if (!user) return;
    
    console.log('🔔 PaymentInfoBanner: Setting up real-time payment method monitoring');
    
    // Poll every 3 seconds as fallback (in case realtime fails)
    const pollInterval = setInterval(async () => {
      try {
        const hasMethod = await userHasVerifiedPaymentMethod(user.id);
        if (hasMethod !== hasPaymentMethod) {
          console.log('📊 PaymentInfoBanner: Poll detected payment method change:', hasMethod);
          setHasPaymentMethod(hasMethod);
        }
      } catch (error) {
        console.error('Error in payment method poll:', error);
      }
    }, 3000);
    
    // Real-time subscription for instant updates
    const channel = supabase
      .channel('payment-methods-updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_payment_methods',
        filter: `user_id=eq.${user.id}`
      }, async (payload) => {
        console.log('🔔 PaymentInfoBanner: Real-time payment method change detected', payload.eventType);
        try {
          const hasMethod = await userHasVerifiedPaymentMethod(user.id);
          setHasPaymentMethod(hasMethod);
          console.log('✅ PaymentInfoBanner: Banner will', hasMethod ? 'HIDE' : 'SHOW');
        } catch (error) {
          console.error('Error refreshing payment method status:', error);
        }
      })
      .subscribe((status) => {
        console.log('📡 PaymentInfoBanner subscription status:', status);
      });
      
    return () => {
      console.log('🧹 PaymentInfoBanner: Cleaning up subscriptions');
      clearInterval(pollInterval);
      supabase.removeChannel(channel);
    };
  }, [user, hasPaymentMethod]);

  const handleGuestAction = () => {
    navigate('/login');
  };

  const handleMemberAction = () => {
    if (onOpenDashboard) {
      onOpenDashboard('payment');
    }
  };

  // Don't show banner if dismissed or if user has payment method
  if (isCheckingPayment) {
    return null;
  }

  // For logged-in users with payment methods, don't show banner
  if (user && hasPaymentMethod === true) {
    return null;
  }

  // Guest user banner (red)
  if (!user) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="relative overflow-hidden"
        >
          <div className="bg-gradient-to-r from-red-600 via-red-500 to-rose-600 border-b border-red-400/20 shadow-xl">
            {/* Decorative elements */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-red-200 rounded-full blur-3xl" />
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
              <div className="py-4 sm:py-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  {/* Left: Icon + Content */}
                  <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5 sm:mt-0">
                      <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-lg">
                        <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0 space-y-3">
                      <div className="flex flex-col gap-1.5">
                        <h3 className="text-white font-semibold text-base sm:text-lg flex items-center gap-2">
                          <Lock className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span>Add Payment Before You Bid</span>
                        </h3>
                        <p className="text-white/95 text-sm sm:text-base leading-relaxed">
                          Payment methods are added only inside your account dashboard. We recommend adding one now so you're ready to participate before or during the auction without disturbtions.
                        </p>
                      </div>
                      
                      {/* Security badges */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 lg:flex lg:items-center lg:gap-3 text-white/90 text-xs">
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                          <Shield className="h-3.5 w-3.5" />
                          <span className="font-medium">Bank-level Encryption</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                          <CheckCircle className="h-3.5 w-3.5" />
                          <span className="font-medium">PCI DSS Compliant</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                          <CreditCard className="h-3.5 w-3.5" />
                          <span className="font-medium">Secure Processing</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                    <Button
                      onClick={handleGuestAction}
                      className="bg-white text-red-600 hover:bg-white/90 shadow-xl hover:shadow-2xl font-semibold px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl transition-all hover:scale-105 flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                      <UserPlus className="h-4 w-4" />
                      <span className="hidden sm:inline">Get Started</span>
                      <span className="sm:hidden">Register</span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Member without payment method banner (informative amber/orange)
  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="relative overflow-hidden"
      >
        <div className="bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 border-b border-amber-400/20 shadow-xl">
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-1/4 w-64 h-64 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-yellow-200 rounded-full blur-3xl" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="py-4 sm:py-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {/* Left: Icon + Content */}
                <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5 sm:mt-0">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-lg">
                      <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0 space-y-3">
                    <div className="flex flex-col gap-1.5">
                      <h3 className="text-white font-semibold text-base sm:text-lg flex items-center gap-2">
                        <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span>Complete Your Account Setup</span>
                      </h3>
                      <p className="text-white/95 text-sm sm:text-base leading-relaxed">
                        Add a payment method to start bidding on exclusive Florida Highwaymen artwork. 
                        Your dashboard gives you full control over payments, bids, and saved items.
                      </p>
                    </div>
                    
                    {/* Dashboard features */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-white/90 text-xs">
                      <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                        <CreditCard className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="font-medium">Payment Methods</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                        <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <span className="font-medium">Bid History</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                        <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                        <span className="font-medium">Saved Lots</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                        <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="font-medium">Profile Settings</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Actions */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                  <Button
                    onClick={handleMemberAction}
                    className="bg-white text-amber-600 hover:bg-white/90 shadow-xl hover:shadow-2xl font-semibold px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl transition-all hover:scale-105 flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    <CreditCard className="h-4 w-4" />
                    <span className="hidden sm:inline">Add Payment Method</span>
                    <span className="sm:hidden">Add Card</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentInfoBanner;
